import type { ValidationRuleCollection } from "@vuelidate/core";
import type { Account, Company } from "@/types";
import type {
  AccountTextKey,
  CompanyTextKey,
  ContactFieldKey,
} from "./fieldKeys";

/** A contact step's rules, by the account or company property they check. */
export type AccountRules = Partial<
  Record<AccountTextKey, ValidationRuleCollection>
>;
export type CompanyRules = Partial<
  Record<CompanyTextKey, ValidationRuleCollection>
>;

/**
 * Everything a `TextRowComponent` needs to edit one contact property, derived from that property's
 * key alone, so its label, value, rules and write-back can't name different fields. Spread it with
 * `v-bind`; per-row extras (`type`, `disabled`) go alongside.
 */
export interface ContactFieldBinding {
  field: ContactFieldKey;
  modelValue: string | null;
  rules: ValidationRuleCollection;
  "onUpdate:modelValue": (value: string) => void;
}

export function accountFieldBinder(
  account: () => Partial<Account>,
  update: (patch: Partial<Account>) => void,
  rules: () => AccountRules
) {
  return (key: AccountTextKey): ContactFieldBinding => ({
    field: `account.${key}`,
    modelValue: account()[key] ?? null,
    rules: rules()[key] ?? {},
    "onUpdate:modelValue": (value) => update({ [key]: value }),
  });
}

/** `fallback` shows while the company has no value of its own, without being written to it. */
export function companyFieldBinder(
  company: () => Partial<Company>,
  update: (patch: Partial<Company>) => void,
  rules: () => CompanyRules
) {
  return (
    key: CompanyTextKey,
    fallback: string | null = null
  ): ContactFieldBinding => ({
    field: `company.${key}`,
    modelValue: company()[key] ?? fallback,
    rules: rules()[key] ?? {},
    "onUpdate:modelValue": (value) => update({ [key]: value }),
  });
}
